import { Controller } from "@hotwired/stimulus"

// Live preview for the Abschlagsrechnung modal: as the user enters a cumulative
// completion (percent or euro net), shows the net amount that will actually be
// billed now (cumulative − already billed) and disables submit when nothing is left.
export default class extends Controller {
  static targets = [
    "basis",
    "percent",
    "amount",
    "percentWrap",
    "amountWrap",
    "percentBtn",
    "amountBtn",
    "deltaOut",
    "submit",
  ]
  static values = { net: Number, billed: Number, currency: String }

  connect() {
    this.usePercent()
  }

  usePercent() {
    this.setBasis("percent")
  }

  useAmount() {
    this.setBasis("amount")
  }

  setBasis(basis) {
    const percent = basis === "percent"
    this.basisTarget.value = basis
    this.setVisible(this.percentWrapTarget, percent)
    this.setVisible(this.amountWrapTarget, !percent)
    if (percent) {
      this.setActive(this.percentBtnTarget, this.amountBtnTarget)
    } else {
      this.setActive(this.amountBtnTarget, this.percentBtnTarget)
    }
    this.update()
  }

  // Hiding a wrap is not enough: an input that is only `display: none` stays in
  // the form and stays constraint-validated. A percent of 1000 left behind in the
  // hidden block made the browser refuse the submit and then fail to show the
  // bubble on an invisible field — the Speichern button did nothing at all.
  // Disabling keeps the value for a switch back and drops it from the request,
  // which is what the controller does with it anyway (#partial_invoice_args).
  // Same idiom as dynamic_form_controller#setVisible.
  setVisible(el, visible) {
    el.classList.toggle("hidden", !visible)
    el.querySelectorAll("input, select, textarea").forEach((input) => {
      input.disabled = !visible
    })
  }

  setActive(on, off) {
    on.classList.add("bg-white", "text-gray-900", "shadow-xs")
    off.classList.remove("bg-white", "text-gray-900", "shadow-xs")
    off.classList.add("text-gray-500")
  }

  update() {
    const raw =
      this.basisTarget.value === "amount"
        ? this.parse(this.amountTarget.value)
        : (this.netValue * this.parse(this.percentTarget.value)) / 100
    const cumulative = this.cents(raw)
    const delta = this.cents(cumulative - this.billedValue)
    // Über 100 % kumuliert lehnt ProgressInvoiceBuilder#resolve_fraction serverseitig
    // ab. Die Vorschau sagt das hier schon, statt einen grünen Betrag anzuzeigen und
    // die Absage dem Klick zu überlassen.
    const valid = delta > 0 && cumulative <= this.cents(this.netValue)

    this.deltaOutTarget.textContent = this.format(delta)
    this.deltaOutTarget.classList.toggle("text-red-600", !valid)
    this.deltaOutTarget.classList.toggle("text-green-700", valid)
    if (this.hasSubmitTarget) this.submitTarget.disabled = !valid
  }

  // Auf Cent runden, bevor verglichen wird: netValue * 100 / 100 trifft netValue in
  // IEEE754 nicht zwingend wieder, sonst gälten glatte 100 % als Überschreitung.
  cents(value) {
    return Math.round(value * 100) / 100
  }

  parse(value) {
    // Percent (HTML number field) uses "." as decimal; the euro text field may use
    // German "1.000,50". Only strip dots when a comma is present.
    let s = String(value || "").trim()
    s = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s
    const n = parseFloat(s)
    return Number.isFinite(n) ? n : 0
  }

  format(value) {
    try {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: this.currencyValue || "EUR",
      }).format(value)
    } catch {
      return value.toFixed(2)
    }
  }
}
